<script lang="ts">
    import type * as t from "~/types";
    import type { Action } from "svelte/action";

    import { derived } from "svelte/store";
    import { getAppContext } from "./quiz-store";
    import { fade, fly } from "svelte/transition";
    import { cn } from "./utils";
    import { onMount } from "svelte";
    import { EventEmitter } from "~/event-emitter";

    export let visible: boolean;

    const ctx = getAppContext();
    const { state, actions } = ctx;
    const currStep = derived(state, ($) => $.step.value);

    const canProceed = derived(state, ($) => {
        let canIt = true;
        switch ($.step.value.type) {
            case "multiple_choice":
                canIt = $.step.value.state?.selectedOption != null;
                break;
            case "educational":
                canIt = !!$.step.value.state?.canProceed;
                break;
            case "construct_minigame":
                canIt = $.step.value.state.gameStatus === "finished";
                break;
        }
        return canIt;
    });

    const continueButtonTitle = derived(state, ($) => {
        const step = $.step.value;
        switch (step.type) {
            case "segment_result":
                if (step.state?.formStatus === "visible")
                    return "Keep Playing >";
            default:
                return "Next >";
        }
    });

    const setupLoadingIndicator: Action = (loadingIndicator) => {
        window.requestAnimationFrame(() => {
            loadingIndicator.classList.replace("w-full", "w-0");
        });
        loadingIndicator.addEventListener("transitionend", () => {
            state.update(($) => {
                switch ($.step.value.type) {
                    case "educational":
                        if ($.step.value.state != null) {
                            $.step.value.state.canProceed = true;
                        }
                    default:
                        return $;
                }
            });
        });
    };

    function handleContentScroll(e: t.AppEvent.OnContentScroll) {
        shadowVisible = e.y < e.target.scrollHeight - e.target.clientHeight;
    }

    function handleContentReady(e: t.AppEvent.OnContentReady) {
        shadowVisible =
            e.target.scrollTop < e.target.scrollHeight - e.target.clientHeight;
    }

    function handleChangeStep() {
        shadowVisible = false;
    }

    onMount(() => {
        ctx.events.on<t.AppEvent.OnContentReady>(
            EventEmitter.EVENT.ON_CONTENT_READY,
            handleContentReady,
        );
        ctx.events.on<t.AppEvent.OnContentScroll>(
            EventEmitter.EVENT.ON_CONTENT_SCROLL,
            handleContentScroll,
        );

        ctx.events.on(EventEmitter.EVENT.ON_CHANGE_STEP, handleChangeStep);

        return () => {
            ctx.events.off(
                EventEmitter.EVENT.ON_CONTENT_READY,
                handleContentReady,
            );
            ctx.events.off(
                EventEmitter.EVENT.ON_CONTENT_SCROLL,
                handleContentScroll,
            );

            ctx.events.off(EventEmitter.EVENT.ON_CHANGE_STEP, handleChangeStep);
        };
    });

    let nextBtn: HTMLButtonElement;
    let loadingEl: HTMLDivElement;
    let shadowVisible = false;
</script>

<footer class={cn("relative w-full px-6 py-4")}>
    {#if visible && shadowVisible}
        <div
            class="pointer-events-none absolute inset-0 size-full rotate-180 rounded-b-2xl shadow-lg"
            transition:fade={{ duration: 100 }}
        ></div>
    {/if}
    <div class="h-8">
        {#if visible}
            <div
                class="flex size-full items-center justify-between text-gray-500"
                transition:fly={{ y: 48 }}
            >
                <span
                    class={cn(
                        "text-2xl font-medium",
                        $state.step.value.type === "segment_result" &&
                            "invisible",
                    )}
                >
                    {$state.step.idx + 1}/{$state.segment.value.steps.length}
                </span>
                <button
                    bind:this={nextBtn}
                    class="next-button relative text-2xl font-bold uppercase"
                    disabled={!$canProceed}
                    on:click={actions.goToNextStep}
                >
                    <span class:opacity-30={!$canProceed}
                        >{$continueButtonTitle}</span
                    >

                    {#if $currStep.type === "educational"}
                        <div
                            bind:this={loadingEl}
                            use:setupLoadingIndicator
                            class="absolute bottom-0 h-px w-full bg-gray-500 transition-[width]"
                            style="transition-duration: {$currStep.waitUntilCanProceed}s;"
                        ></div>
                    {/if}
                </button>
            </div>
        {/if}
    </div>
</footer>
